import { makeAutoObservable } from 'mobx'
import { addDays } from 'date-fns/addDays'
import { differenceInDays } from 'date-fns/differenceInDays'
import { endOfMonth } from 'date-fns/endOfMonth'
import { isBefore } from 'date-fns/isBefore'
import { startOfToday } from 'date-fns/startOfToday'

import type { Account, Recurrence, UserProfile } from '../types'
import { getAccountStartingBalance } from '../utils/AccountUtils'

import { GlobalStore } from './GlobalStore'

export class CashflowStore {
  globalStore: GlobalStore
  uiStore: UiStore

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.uiStore = new UiStore(globalStore)

    makeAutoObservable(this)
  }

  completeOnboarding = async () => {
    const updatedUserProfile = {
      ...this.globalStore.userStore.userProfile,
      has_completed_cash_flow_onboarding: true,
    } as UserProfile

    await this.globalStore.userStore.updateUserProfile(updatedUserProfile)
  }
}

class UiStore {
  globalStore: GlobalStore

  // Cashflow is hard-coded to display 30 days starting from today. This is the
  // single source of truth for Cash Flow's window; it intentionally does NOT
  // mutate RecurringTransactionsStore.dateRange, so Cash Flow stays decoupled
  // from other widgets (e.g. Recurring Transactions) that drive that shared range.
  dateRange = { start: startOfToday(), end: addDays(startOfToday(), 30) }
  isDirty = false

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore

    makeAutoObservable(this)
  }

  // Recurrence data scoped to Cash Flow's own dateRange, sourced from the
  // range-parameterized helpers on RecurringTransactionsStore. Computeds (not
  // component state) per the project's MobX conventions.
  get income(): Recurrence[] {
    return this.globalStore.recurringTransactionsStore.incomeForRange(this.dateRange)
  }

  get upcomingIncome(): Recurrence[] {
    return this.globalStore.recurringTransactionsStore.upcomingIncomeForRange(this.dateRange)
  }

  get upcomingExpenses(): Recurrence[] {
    return this.globalStore.recurringTransactionsStore.upcomingExpensesForRange(this.dateRange)
  }

  get nextIncome(): Recurrence | undefined {
    return this.globalStore.recurringTransactionsStore.nextIncomeForRange(this.dateRange)
  }

  // Visible cash accounts that are currently selected in the account filter.
  // Computed (single source of truth) so it stays in sync with account data and
  // the filter without effect-based syncing in components.
  get selectedCashAccounts(): Account[] {
    const { selectedAccountGuids } = this.globalStore.globalUiStore
    return this.globalStore.accountStore.visibleCashAccounts.filter((account) =>
      selectedAccountGuids.includes(account.guid),
    )
  }

  // Total spendable/starting balance across the selected cash accounts, using
  // the shared per-account balance rule (available vs current balance).
  get selectedCashBalance(): number {
    const showAvailableBalance =
      this.globalStore.appConfig.config.display_available_balance_in_accounts
    return this.selectedCashAccounts.reduce(
      (total, account) => total + getAccountStartingBalance(account, showAvailableBalance),
      0,
    )
  }
  // Returns the number of days until the next income activity or 0 if there is no income activity
  get daysUntilNextIncome() {
    return this.nextIncome
      ? Math.abs(differenceInDays(startOfToday(), this.nextIncome.expectedDate))
      : 0
  }

  // Returns the expenses (Activities) until the  next income activity or the end of the month
  // if there is no income activity
  get expensesTilNextIncome() {
    return this.upcomingExpenses.filter((a) =>
      isBefore(a.expectedDate, this.nextIncome?.expectedDate || endOfMonth(new Date())),
    )
  }
}
